const PAPER_ID_KEYS = ["paper_id", "paperId", "Paper_id", "PaperId"];
const OPTION_LETTERS = ["A", "B", "C", "D", "E", "F", "G", "H"];

const readPaperId = (source) => {
  if (!source || typeof source !== "object") return null;

  for (const key of PAPER_ID_KEYS) {
    const value = source[key];
    if (value != null && value !== "") {
      return value;
    }
  }

  return null;
};

export const resolveExercisePaperId = (
  testDetails = {},
  questions = [],
  apiPayload = {},
  apiRoot = {}
) => {
  const fromQuestion = (questions || [])
    .map((question) => readPaperId(question))
    .find(Boolean);

  return (
    readPaperId(testDetails) ??
    readPaperId(apiPayload) ??
    readPaperId(apiRoot) ??
    readPaperId(apiRoot?.data) ??
    fromQuestion ??
    null
  );
};

export const withResolvedPaperId = (testDetails = {}, paperId) =>
  paperId != null && paperId !== ""
    ? { ...testDetails, paper_id: paperId }
    : testDetails;

export const normalizeExerciseQuestions = (questions = [], paperId) =>
  (questions || []).map((question) => ({
    ...question,
    paper_id: question?.paper_id ?? paperId,
  }));

export const isTopicExerciseFlow = (testDetails = {}) => {
  if (testDetails?.redirectedFrom === "activity") {
    return true;
  }

  const exerciseType = String(
    testDetails?.exerciseType || testDetails?.excersie_type || ""
  ).toLowerCase();

  return (
    Boolean(testDetails?.excersie_type) ||
    exerciseType === "exercise" ||
    exerciseType === "dpp"
  );
};

const normalizeAnswerType = (question = {}) => {
  const raw = String(question?.answer_type || question?.type || "").toLowerCase();
  if (raw.includes("integer")) return "integer";
  if (raw.includes("multiple")) return "multiple";
  if (raw.includes("single")) return "single";
  return "single";
};

export const parseCorrectAnswerLetters = (answer) => {
  if (answer == null || answer === "") return [];

  return String(answer)
    .split(/[,|]/)
    .map((value) => value.trim().toUpperCase())
    .filter(Boolean);
};

export const getCorrectOptionIds = (question = {}) => {
  const options = question?.option || [];
  const fromIsCorrect = options
    .filter((option) => Number(option.is_correct) === 1)
    .map((option) => option.option_id);

  if (fromIsCorrect.length) {
    return fromIsCorrect;
  }

  const letters = parseCorrectAnswerLetters(question?.answer);
  if (!letters.length) {
    return [];
  }

  return letters
    .map((letter) => {
      const index = OPTION_LETTERS.indexOf(letter);
      return index >= 0 && options[index] ? options[index].option_id : null;
    })
    .filter((optionId) => optionId != null);
};

export const getUserSelectedOptionIds = (question = {}) =>
  (question?.userAnswerCollection || [])
    .map((answer) => answer?.option_id)
    .filter((optionId) => optionId != null);

const arraysEqual = (left = [], right = []) => {
  if (left.length !== right.length) return false;
  const leftSorted = [...left].map(String).sort();
  const rightSorted = [...right].map(String).sort();
  return leftSorted.every((value, index) => value === rightSorted[index]);
};

export const evaluateExerciseQuestion = (question = {}) => {
  const answerType = normalizeAnswerType(question);

  if (answerType === "integer") {
    const userAnswer = String(question?.integer_answer ?? "").trim();
    const correctAnswer = String(question?.answer ?? "").trim();

    if (!userAnswer) {
      return { isAttempted: false, isCorrect: false };
    }

    const isCorrect =
      userAnswer === correctAnswer ||
      Number(userAnswer) === Number(correctAnswer);

    return { isAttempted: true, isCorrect };
  }

  const userOptionIds = getUserSelectedOptionIds(question);
  if (!userOptionIds.length) {
    return { isAttempted: false, isCorrect: false };
  }

  const correctOptionIds = getCorrectOptionIds(question);

  if (answerType === "multiple") {
    return {
      isAttempted: true,
      isCorrect: arraysEqual(userOptionIds, correctOptionIds),
    };
  }

  return {
    isAttempted: true,
    isCorrect:
      userOptionIds.length === 1 &&
      correctOptionIds.map(String).includes(String(userOptionIds[0])),
  };
};

export const initExerciseQuestionClientState = (question = {}) => {
  const correctOptionIds = getCorrectOptionIds(question);

  question.isReview = false;
  question.isAttempted = false;
  question.isAnswerGuessed = false;
  question.totalTimeTaken = 0;
  question.isAnswerCorrect = 0;
  question.correctAnswerIdCollection = [...correctOptionIds];
  question.wrongAnswerIdCollection = [];
  question.userAnswerCollection = [];
  question.clientCorrectAnswer = question.answer ?? null;

  return question;
};

export const applyQuestionAnswerEvaluation = (question = {}) => {
  const { isAttempted, isCorrect } = evaluateExerciseQuestion(question);
  const correctOptionIds = getCorrectOptionIds(question);
  const userOptionIds = getUserSelectedOptionIds(question);

  question.isAttempted = isAttempted;
  question.isAnswerCorrect = isCorrect ? 1 : 0;
  question.correctAnswerIdCollection = [...correctOptionIds];
  question.wrongAnswerIdCollection = userOptionIds.filter(
    (optionId) => !correctOptionIds.map(String).includes(String(optionId))
  );

  return question;
};

export const evaluateExerciseQuestions = (questions = []) =>
  (questions || []).map((question) =>
    applyQuestionAnswerEvaluation({ ...question })
  );

export const buildLocalExerciseSummary = (questions = []) => {
  const evaluatedQuestions = evaluateExerciseQuestions(questions);
  const total_questions = evaluatedQuestions.length;
  const attempted = evaluatedQuestions.filter(
    (question) => question.isAttempted
  ).length;
  const correct_answer = evaluatedQuestions.filter(
    (question) => Number(question.isAnswerCorrect) === 1
  ).length;
  const incorrect_answer = Math.max(attempted - correct_answer, 0);
  const marksPerQuestion = 4;
  const total_marks = marksPerQuestion * total_questions;
  const earned_marks = correct_answer * marksPerQuestion;
  const score_percentage =
    total_marks > 0
      ? Number(((earned_marks / total_marks) * 100).toFixed(2))
      : 0;

  return {
    total_questions,
    total_marks,
    earned_marks,
    correct_answer,
    incorrect_answer,
    unattempted: Math.max(total_questions - attempted, 0),
    score_percentage,
    evaluatedQuestions,
  };
};

export const extractPaperIdFromResponse = (response) =>
  readPaperId(response?.data?.data) ?? readPaperId(response?.data) ?? null;

export const getExerciseApiPayload = (apiRoot = {}) => apiRoot?.data ?? apiRoot;

export const buildTopicExerciseSavePayload = ({
  testDetails,
  question,
  userPreference,
  topicData,
  userData,
  testType,
  calculatedTime,
}) => {
  const evaluatedQuestion = applyQuestionAnswerEvaluation({ ...question });
  const filteredOptions = evaluatedQuestion?.userAnswerCollection?.map(
    (option) => option.option_id
  );
  const answerType = normalizeAnswerType(evaluatedQuestion);
  const typeId =
    evaluatedQuestion?.type_id || (answerType === "integer" ? 2 : answerType === "multiple" ? 7 : 6);

  const payload = {
    exercise_type: testDetails.excersie_type,
    course_id: userPreference.courseDetails[0].course_id,
    topic_id: topicData.topicId,
    qid: evaluatedQuestion.qid,
    is_saved: evaluatedQuestion.is_saved ? 1 : 0,
    total_time: calculatedTime ?? evaluatedQuestion.totalTimeTaken ?? 0,
    type_id: typeId,
    user_id: userData.user_id,
  };

  if (testType === "dpp") {
    payload.video_id = testDetails.videoId;
  }

  if (answerType === "integer") {
    payload.integer_answer = evaluatedQuestion.integer_answer ?? "";
    payload.right_answer = Number(evaluatedQuestion.isAnswerCorrect) === 1;
    payload.wrong_answer = Number(evaluatedQuestion.isAnswerCorrect) !== 1;
  } else if (answerType === "multiple") {
    payload.answer = filteredOptions?.length ? filteredOptions : [];
    payload.right_answer = Number(evaluatedQuestion.isAnswerCorrect) === 1;
    payload.wrong_answer = Number(evaluatedQuestion.isAnswerCorrect) !== 1;
  } else {
    payload.answer = filteredOptions?.length ? filteredOptions : [];
    payload.right_answer = Number(evaluatedQuestion.isAnswerCorrect) === 1;
    payload.wrong_answer = Number(evaluatedQuestion.isAnswerCorrect) !== 1;
  }

  return payload;
};

export const getQuestionsWithUpdatedTime = (
  questions = [],
  currentQuestion,
  calculatedTime
) =>
  (questions || []).map((question, index) =>
    index === currentQuestion
      ? { ...question, totalTimeTaken: calculatedTime }
      : question
  );

export const hasAttemptedAnswer = (question = {}) =>
  evaluateExerciseQuestion(question).isAttempted;

export const buildTopicExerciseSubmitSummary = (
  questions = [],
  currentQuestion,
  calculatedTime
) => {
  const questionsForSummary = getQuestionsWithUpdatedTime(
    questions,
    currentQuestion,
    calculatedTime
  );
  const summaryResult = buildLocalExerciseSummary(questionsForSummary);
  const { evaluatedQuestions, ...localSummary } = summaryResult;

  return {
    localSummary,
    evaluatedQuestions,
    questionsForSummary,
  };
};

export const EXERCISE_SUMMARY_STORAGE_KEY = "topicExerciseLocalSummary";

export const persistExerciseSummary = (localSummary) => {
  try {
    sessionStorage.setItem(
      EXERCISE_SUMMARY_STORAGE_KEY,
      JSON.stringify(localSummary)
    );
  } catch (error) {
    console.log(error);
  }
};

export const readPersistedExerciseSummary = () => {
  try {
    const stored = sessionStorage.getItem(EXERCISE_SUMMARY_STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    return null;
  }
};

export const buildExerciseProgressStorageKey = ({
  userId,
  courseId,
  topicId,
  exerciseType,
}) => {
  if (!userId || !courseId || !topicId || !exerciseType) {
    return null;
  }

  return `topicExerciseProgress:${userId}:${courseId}:${topicId}:${exerciseType}`;
};

const writeExerciseProgressPayload = (storage, key, payload) => {
  storage.setItem(key, JSON.stringify(payload));
};

export const persistExerciseProgress = ({
  userId,
  courseId,
  topicId,
  exerciseType,
  questions = [],
  currentQuestion = 0,
  timerSeconds,
}) => {
  const key = buildExerciseProgressStorageKey({
    userId,
    courseId,
    topicId,
    exerciseType,
  });
  if (!key) return;

  const payload = {
    currentQuestion,
    timerSeconds: Number(timerSeconds) || 0,
    updatedAt: Date.now(),
    answers: questions.map((question) => ({
      qid: question.qid,
      userAnswerCollection: question.userAnswerCollection || [],
      integer_answer: question.integer_answer ?? "",
      isAttempted: Boolean(question.isAttempted),
      isAnswerCorrect: question.isAnswerCorrect ?? 0,
      totalTimeTaken: question.totalTimeTaken ?? 0,
      isReview: Boolean(question.isReview || question.marked),
      marked: Boolean(question.marked),
      visited: Boolean(question.visited),
      is_saved: question.is_saved,
      isAnswerGuessed: Boolean(question.isAnswerGuessed),
    })),
  };

  try {
    writeExerciseProgressPayload(localStorage, key, payload);
  } catch (error) {
    console.log(error);
  }

  try {
    writeExerciseProgressPayload(sessionStorage, key, payload);
  } catch (error) {
    console.log(error);
  }
};

export const readExerciseProgress = ({
  userId,
  courseId,
  topicId,
  exerciseType,
}) => {
  const key = buildExerciseProgressStorageKey({
    userId,
    courseId,
    topicId,
    exerciseType,
  });
  if (!key) return null;

  try {
    const stored = localStorage.getItem(key) || sessionStorage.getItem(key);
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    return null;
  }
};

export const clearExerciseProgress = ({
  userId,
  courseId,
  topicId,
  exerciseType,
}) => {
  const key = buildExerciseProgressStorageKey({
    userId,
    courseId,
    topicId,
    exerciseType,
  });
  if (!key) return;

  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.log(error);
  }

  try {
    sessionStorage.removeItem(key);
  } catch (error) {
    console.log(error);
  }
};

export const applyExerciseProgress = (questions = [], savedProgress) => {
  if (!savedProgress?.answers?.length) {
    return questions;
  }

  const savedByQid = new Map(
    savedProgress.answers.map((answer) => [String(answer.qid), answer])
  );

  return questions.map((question) => {
    const saved = savedByQid.get(String(question.qid));
    if (!saved) {
      return question;
    }

    const marked = Boolean(saved.marked || saved.isReview);

    const mergedQuestion = {
      ...question,
      userAnswerCollection: saved.userAnswerCollection || [],
      integer_answer: saved.integer_answer ?? "",
      isAttempted: saved.isAttempted,
      isAnswerCorrect: saved.isAnswerCorrect ?? 0,
      totalTimeTaken: saved.totalTimeTaken ?? 0,
      isReview: marked,
      marked,
      visited: saved.visited === true || marked,
      is_saved: saved.is_saved,
      isAnswerGuessed: saved.isAnswerGuessed,
    };

    return applyQuestionAnswerEvaluation(mergedQuestion);
  });
};

export const resolveResumeQuestionIndex = ({
  savedProgress,
  apiResumeIndex,
  totalQuestions,
}) => {
  const fromStorage =
    savedProgress?.currentQuestion != null ? savedProgress.currentQuestion : null;
  const fromApi = apiResumeIndex >= 0 ? apiResumeIndex : 0;
  const nextIndex = fromStorage ?? fromApi;

  return Math.min(Math.max(nextIndex, 0), Math.max(totalQuestions - 1, 0));
};

export const getExerciseProgressParams = ({
  testDetails,
  userData,
  userPreference,
  topicData,
}) => {
  if (!isTopicExerciseFlow(testDetails)) {
    return null;
  }

  return {
    userId: userData?.user_id,
    courseId: userPreference?.courseDetails?.[0]?.course_id,
    topicId: topicData?.topicId,
    exerciseType: testDetails?.excersie_type,
  };
};

const buildSubmittedExercisesStorageKey = ({ userId, courseId, topicId }) => {
  if (!userId || !courseId || !topicId) {
    return null;
  }

  return `topicExerciseSubmitted:${userId}:${courseId}:${topicId}`;
};

export const readSubmittedExerciseTypes = ({ userId, courseId, topicId }) => {
  const key = buildSubmittedExercisesStorageKey({ userId, courseId, topicId });
  if (!key) return [];

  try {
    const stored = localStorage.getItem(key);
    const parsed = stored ? JSON.parse(stored) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
};

export const isExerciseSubmittedLocally = ({
  userId,
  courseId,
  topicId,
  exerciseType,
}) => {
  if (!exerciseType) return false;

  return readSubmittedExerciseTypes({ userId, courseId, topicId }).includes(
    exerciseType
  );
};

export const markExerciseAsSubmitted = ({
  userId,
  courseId,
  topicId,
  exerciseType,
}) => {
  const key = buildSubmittedExercisesStorageKey({ userId, courseId, topicId });
  if (!key || !exerciseType) return;

  try {
    const existing = readSubmittedExerciseTypes({ userId, courseId, topicId });
    if (existing.includes(exerciseType)) return;

    localStorage.setItem(key, JSON.stringify([...existing, exerciseType]));
  } catch (error) {
    console.log(error);
  }
};

export const clearSubmittedExercise = ({
  userId,
  courseId,
  topicId,
  exerciseType,
}) => {
  const key = buildSubmittedExercisesStorageKey({ userId, courseId, topicId });
  if (!key || !exerciseType) return;

  try {
    const existing = readSubmittedExerciseTypes({ userId, courseId, topicId });
    const next = existing.filter((type) => type !== exerciseType);
    localStorage.setItem(key, JSON.stringify(next));
  } catch (error) {
    console.log(error);
  }
};

export const isStartPracticeExercise = (exercise, context = {}) => {
  if (!exercise) return false;

  const submittedLocally = isExerciseSubmittedLocally({
    ...context,
    exerciseType: exercise.excersie_type,
  });

  return exercise.is_attempt === false || submittedLocally;
};

export const isResumePracticeExercise = (exercise, context = {}) => {
  if (!exercise) return false;

  const submittedLocally = isExerciseSubmittedLocally({
    ...context,
    exerciseType: exercise.excersie_type,
  });

  return exercise.is_attempt === true && !submittedLocally;
};
